import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../../../shared/core/logging/logger";
import { cardManager } from "../../../../../../shared/core/service/services";
import type { Card } from "../../../../../../shared/domains/cards/card";
import { CardStatus } from "../../../../../../shared/domains/cards/card";
import { pollCardStatus } from "../../../../../../shared/domains/cards/poll-card-status";
import { ToggleRow } from "../../../../common/action-row";
import { ErrorMessage } from "../../../../common/error-message";

interface LockCardSwitchProps {
  card: Card;
  loading: boolean;
  disabled?: boolean;
  block: () => Promise<void>;
  unblock: () => Promise<void>;
  className?: string;
}

export function LockCardSwitch(props: LockCardSwitchProps) {
  const { card, loading, disabled, block, unblock, className } = props;
  const { formatMessage } = useIntl();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [isLocked, setIsLocked] = useState(card.status === CardStatus.Blocked);

  useEffect(() => {
    setIsLocked(card.status === CardStatus.Blocked);
  }, [card.status, setIsLocked]);

  useEffect(() => {
    setError(null);
  }, [card]);

  const isCardBlockable = card.status === CardStatus.Active || card.status === CardStatus.Blocked;

  const blockOrUnblock = async (lock: boolean) => {
    if (loading || pending) {
      return;
    }
    setError(null);
    setPending(true);
    setIsLocked(lock);
    try {
      // The API answers before the card has moved, so the new status is polled for rather than
      // assumed: the switch would otherwise flip back on the next refresh.
      if (lock) {
        await block();
        await pollCardStatus(card.id, CardStatus.Blocked, (id) => cardManager.retrieveCard(id));
      } else {
        await unblock();
        await pollCardStatus(card.id, CardStatus.Active, (id) => cardManager.retrieveCard(id));
      }
      await cardManager.refresh();
    } catch (e) {
      setIsLocked(!lock);
      logger.debug(e);
      setError(e?.response?.data?.error?.message);
    } finally {
      setPending(false);
    }
  };

  return isCardBlockable ? (
    <>
      <ToggleRow
        className={className}
        label={formatMessage("cardOptions.switch.lockCard")}
        value={isLocked}
        disabled={disabled || pending}
        onSwitch={blockOrUnblock}
      />
      {error && <StyledErrorMessage>{error}</StyledErrorMessage>}
    </>
  ) : null;
}

const StyledErrorMessage = styled(ErrorMessage)`
  margin: 10px 0;
`;
